// db.js — typed-ish data access layer over Supabase. Every function returns
// { data, error } so the UI can render error states cleanly.

import { supabase } from "./supabase";

function unwrap(p) {
  return p.then(({ data, error }) => {
    if (error) throw error;
    return data;
  });
}

// ============================================================================
// APPLIANCES
// ============================================================================

export async function listAppliances(userId) {
  return unwrap(
    supabase.from("appliances").select("*").eq("user_id", userId).order("created_at")
  );
}

export async function addAppliance(userId, payload) {
  return unwrap(
    supabase.from("appliances").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function updateAppliance(id, updates) {
  return unwrap(
    supabase.from("appliances").update(updates).eq("id", id).select().single()
  );
}

export async function deleteAppliance(id) {
  return unwrap(supabase.from("appliances").delete().eq("id", id));
}

// ============================================================================
// PANTRY: SKUs + INVENTORY
// ============================================================================

export async function listSkus(userId) {
  return unwrap(
    supabase.from("pantry_skus").select("*").eq("user_id", userId).order("canonical_name")
  );
}

export async function findSkuByBarcode(userId, barcode) {
  const { data, error } = await supabase
    .from("pantry_skus")
    .select("*")
    .eq("user_id", userId)
    .eq("barcode", barcode)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertSku(userId, sku) {
  // If barcode exists for this user, return existing; otherwise insert
  if (sku.barcode) {
    const existing = await findSkuByBarcode(userId, sku.barcode);
    if (existing) return existing;
  }
  return unwrap(
    supabase.from("pantry_skus").insert({ ...sku, user_id: userId }).select().single()
  );
}

export async function updateSku(id, updates) {
  return unwrap(
    supabase.from("pantry_skus").update(updates).eq("id", id).select().single()
  );
}

export async function deleteSku(id) {
  // cascade deletes inventory entries via FK
  return unwrap(supabase.from("pantry_skus").delete().eq("id", id));
}

export async function listInventory(userId) {
  return unwrap(
    supabase.from("pantry_inventory").select("*").eq("user_id", userId).order("expires_at")
  );
}

export async function addInventoryEntry(userId, entry) {
  return unwrap(
    supabase.from("pantry_inventory").insert({ ...entry, user_id: userId }).select().single()
  );
}

export async function deleteInventoryEntry(id) {
  return unwrap(supabase.from("pantry_inventory").delete().eq("id", id));
}

export async function updateInventoryEntry(id, updates) {
  return unwrap(
    supabase.from("pantry_inventory").update(updates).eq("id", id).select().single()
  );
}

// ============================================================================
// RECIPES
// ============================================================================

export async function listRecipes(userId) {
  return unwrap(
    supabase.from("recipes").select("*").eq("user_id", userId)
      .order("favorite", { ascending: false }).order("created_at", { ascending: false })
  );
}

export async function addRecipe(userId, payload) {
  return unwrap(
    supabase.from("recipes").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function updateRecipe(id, updates) {
  return unwrap(
    supabase.from("recipes").update(updates).eq("id", id).select().single()
  );
}

export async function deleteRecipe(id) {
  return unwrap(supabase.from("recipes").delete().eq("id", id));
}

// Persist a new order for the user's favorite recipes. Each id gets
// favorite_order = its index in the array.
export async function reorderFavoriteRecipes(userId, orderedIds) {
  await Promise.all(
    orderedIds.map((id, idx) =>
      unwrap(
        supabase.from("recipes")
          .update({ favorite_order: idx })
          .eq("id", id)
          .eq("user_id", userId)
      )
    )
  );
}

export async function markRecipeMade(id, currentTimes) {
  return unwrap(
    supabase.from("recipes").update({
      times_made: (currentTimes || 0) + 1,
      last_made_at: new Date().toISOString(),
    }).eq("id", id).select().single()
  );
}

// ============================================================================
// BEANS
// ============================================================================

export async function listBeans(userId) {
  return unwrap(
    supabase.from("beans").select("*").eq("user_id", userId)
      .order("sort_order", { ascending: true, nullsFirst: true })
      .order("created_at", { ascending: false })
  );
}

// Persist a new bag order. orderedIds must include every bean the user owns,
// in the desired display order. Each bean gets sort_order = its index.
export async function reorderBeans(userId, orderedIds) {
  await Promise.all(
    orderedIds.map((id, idx) =>
      unwrap(
        supabase.from("beans")
          .update({ sort_order: idx })
          .eq("id", id)
          .eq("user_id", userId)
      )
    )
  );
}

export async function addBean(userId, payload) {
  return unwrap(
    supabase.from("beans").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function updateBean(id, updates) {
  return unwrap(supabase.from("beans").update(updates).eq("id", id).select().single());
}

export async function deleteBean(id) {
  return unwrap(supabase.from("beans").delete().eq("id", id));
}

// ============================================================================
// BREW LOGS
// ============================================================================

export async function listBrewLogs(userId, limit = 50) {
  return unwrap(
    supabase.from("brew_logs").select("*").eq("user_id", userId)
      .order("brewed_at", { ascending: false }).limit(limit)
  );
}

export async function addBrewLog(userId, payload) {
  return unwrap(
    supabase.from("brew_logs").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function updateBrewLog(id, updates) {
  return unwrap(
    supabase.from("brew_logs").update(updates).eq("id", id).select().single()
  );
}

export async function deleteBrewLog(id) {
  const { error } = await supabase.from("brew_logs").delete().eq("id", id);
  if (error) throw error;
}

// ============================================================================
// AI USAGE LOGS
// ============================================================================

export async function logAiUsage(userId, payload) {
  // Best-effort: failures shouldn't break the calling AI feature
  try {
    await supabase.from("ai_usage_logs").insert({ ...payload, user_id: userId });
  } catch (e) {
    console.warn("ai usage log failed", e);
  }
}

export async function getAiUsageSince(userId, sinceIso) {
  return unwrap(
    supabase.from("ai_usage_logs").select("*")
      .eq("user_id", userId)
      .gte("created_at", sinceIso)
      .order("created_at", { ascending: false })
  );
}

// ============================================================================
// PREFERENCES
// ============================================================================

export async function getPreferences(userId) {
  const { data, error } = await supabase
    .from("preferences").select("*").eq("user_id", userId).maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertPreferences(userId, prefs) {
  return unwrap(
    supabase.from("preferences")
      .upsert({ ...prefs, user_id: userId }, { onConflict: "user_id" })
      .select().single()
  );
}

// ============================================================================
// COFFEE FAVORITES
// ============================================================================

export async function listCoffeeFavorites(userId) {
  const { data, error } = await supabase
    .from("coffee_favorites").select("*")
    .eq("user_id", userId)
    .order("last_used_at", { ascending: false, nullsFirst: false });
  if (error) throw error;
  return data || [];
}

export async function addCoffeeFavorite(userId, payload) {
  return unwrap(
    supabase.from("coffee_favorites").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function bumpCoffeeFavorite(favId) {
  // Increments use_count and sets last_used_at — we use rpc-less optimistic update.
  const { data: existing, error: e1 } = await supabase
    .from("coffee_favorites").select("use_count").eq("id", favId).single();
  if (e1) throw e1;
  return unwrap(
    supabase.from("coffee_favorites")
      .update({ use_count: (existing?.use_count || 0) + 1, last_used_at: new Date().toISOString() })
      .eq("id", favId).select().single()
  );
}

export async function deleteCoffeeFavorite(favId) {
  const { error } = await supabase.from("coffee_favorites").delete().eq("id", favId);
  if (error) throw error;
}

// ============================================================================
// TASTE PROFILE
// ============================================================================

export async function getTasteProfile(userId) {
  const { data, error } = await supabase
    .from("coffee_taste_profile").select("*").eq("user_id", userId).maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertTasteProfile(userId, profile) {
  return unwrap(
    supabase.from("coffee_taste_profile")
      .upsert({ ...profile, user_id: userId }, { onConflict: "user_id" })
      .select().single()
  );
}

// ============================================================================
// CHECK-INS
// ============================================================================

export async function listCheckIns(userId, limit = 50) {
  const { data, error } = await supabase
    .from("coffee_check_ins").select("*")
    .eq("user_id", userId)
    .order("checked_in_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function addCheckIn(userId, payload) {
  return unwrap(
    supabase.from("coffee_check_ins").insert({ ...payload, user_id: userId }).select().single()
  );
}

// ============================================================================
// SHOPPING LIST
// ============================================================================

export async function listShoppingItems(userId, includeDone = false) {
  let q = supabase.from("shopping_list").select("*").eq("user_id", userId);
  if (!includeDone) q = q.eq("done", false);
  const { data, error } = await q.order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function addShoppingItem(userId, payload) {
  return unwrap(
    supabase.from("shopping_list").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function setShoppingItemDone(itemId, done) {
  return unwrap(
    supabase.from("shopping_list")
      .update({ done, done_at: done ? new Date().toISOString() : null })
      .eq("id", itemId).select().single()
  );
}

export async function updateShoppingItem(itemId, updates) {
  return unwrap(
    supabase.from("shopping_list").update(updates).eq("id", itemId).select().single()
  );
}

export async function deleteShoppingItem(itemId) {
  const { error } = await supabase.from("shopping_list").delete().eq("id", itemId);
  if (error) throw error;
}

// ============================================================================
// BOTTOMLESS IMPORTS
// ============================================================================

export async function listBottomlessImports(userId, status = null) {
  let q = supabase.from("bottomless_imports").select("*").eq("user_id", userId);
  if (status) q = q.eq("status", status);
  const { data, error } = await q.order("received_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateBottomlessImport(importId, patch) {
  return unwrap(
    supabase.from("bottomless_imports").update(patch).eq("id", importId).select().single()
  );
}

// ============================================================================
// COOK EVENTS
// ============================================================================

export async function listCookEvents(userId, recipeId = null, limit = 100) {
  let q = supabase.from("cook_events").select("*").eq("user_id", userId);
  if (recipeId) q = q.eq("recipe_id", recipeId);
  const { data, error } = await q.order("cooked_at", { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}

export async function addCookEvent(userId, payload) {
  return unwrap(
    supabase.from("cook_events").insert({ ...payload, user_id: userId }).select().single()
  );
}

// One-off: set/unset is_default_for_bean on a favorite
export async function upsertFavoriteDefault(favId, isDefault) {
  return unwrap(
    supabase.from("coffee_favorites")
      .update({ is_default_for_bean: !!isDefault })
      .eq("id", favId).select().single()
  );
}

// ============================================================================
// PANTRY INBOX (receipts queue)
// ============================================================================

export async function listPantryInbox(userId, status = null) {
  let q = supabase.from("pantry_inbox").select("*")
    .eq("user_id", userId)
    .order("received_at", { ascending: false });
  if (status) q = q.eq("status", status);
  return unwrap(q);
}

export async function getPantryInbox(id) {
  return unwrap(supabase.from("pantry_inbox").select("*").eq("id", id).single());
}

export async function addPantryInbox(userId, payload) {
  return unwrap(
    supabase.from("pantry_inbox").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function updatePantryInbox(id, patch) {
  return unwrap(
    supabase.from("pantry_inbox").update(patch).eq("id", id).select().single()
  );
}

export async function deletePantryInbox(id) {
  return unwrap(supabase.from("pantry_inbox").delete().eq("id", id));
}

// ============================================================================
// COFFEE INBOX (shipments queue)
// ============================================================================

export async function listCoffeeInbox(userId, status = null) {
  let q = supabase.from("coffee_inbox").select("*")
    .eq("user_id", userId)
    .order("received_at", { ascending: false });
  if (status) q = q.eq("status", status);
  return unwrap(q);
}

export async function getCoffeeInbox(id) {
  return unwrap(supabase.from("coffee_inbox").select("*").eq("id", id).single());
}

export async function addCoffeeInbox(userId, payload) {
  return unwrap(
    supabase.from("coffee_inbox").insert({ ...payload, user_id: userId }).select().single()
  );
}

export async function updateCoffeeInbox(id, patch) {
  return unwrap(
    supabase.from("coffee_inbox").update(patch).eq("id", id).select().single()
  );
}

export async function deleteCoffeeInbox(id) {
  return unwrap(supabase.from("coffee_inbox").delete().eq("id", id));
}

// ============================================================================
// RECIPE INBOX (forwarded recipe emails queue)
// ============================================================================

export async function listRecipeInbox(userId, status = null) {
  let q = supabase.from("recipe_inbox").select("*")
    .eq("user_id", userId)
    .order("received_at", { ascending: false });
  if (status) q = q.eq("status", status);
  return unwrap(q);
}

export async function updateRecipeInbox(id, patch) {
  return unwrap(
    supabase.from("recipe_inbox").update(patch).eq("id", id).select().single()
  );
}

export async function deleteRecipeInbox(id) {
  return unwrap(supabase.from("recipe_inbox").delete().eq("id", id));
}
